import { ReactElement, useCallback, useSyncExternalStore } from 'react'
import { useTheme } from 'styled-components'

import { Carat, RotateClockwise } from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'

import { CarouselControlsButtonWrapper, StyledIconButton } from './styles'

import type { Swiper as SwiperInstance } from 'swiper'

export const CarouselControls = ({ swiper }: { swiper: SwiperInstance | null }): ReactElement => {
  const {
    cart: {
      upsell: {
        accessibility: { carouselNextButton, carouselBackButton },
      },
    },
  } = useTranslation()
  const { color } = useTheme()

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!swiper) return () => {}
      // Defer the initial read so Swiper's container has its final dimensions
      // before we snapshot isBeginning/isEnd. Without this, the snapshot can be
      // stale if the MiniCart's open transition hasn't finished when onSwiper fires.
      const rafId = requestAnimationFrame(onStoreChange)
      swiper.on('activeIndexChange', onStoreChange)
      return () => {
        cancelAnimationFrame(rafId)
        swiper.off('activeIndexChange', onStoreChange)
      }
    },
    [swiper]
  )

  const isBeginning = useSyncExternalStore(
    subscribe,
    () => swiper?.isBeginning ?? true,
    () => true
  )
  const isEnd = useSyncExternalStore(
    subscribe,
    () => swiper?.isEnd ?? false,
    () => false
  )

  return (
    <CarouselControlsButtonWrapper>
      <StyledIconButton
        onClick={() => swiper?.slidePrev()}
        variant='black-on-gray'
        aria-label={carouselBackButton}
        aria-disabled={isBeginning}
      >
        <Carat
          stroke={isBeginning ? color.stroke.neutral.border.decoration.on.strong : 'currentColor'}
          style={{ minWidth: '9px', minHeight: '9px' }}
          rotateClockwise={RotateClockwise.Half}
        />
      </StyledIconButton>
      <StyledIconButton
        onClick={() => swiper?.slideNext()}
        aria-disabled={isEnd}
        variant='black-on-gray'
        aria-label={carouselNextButton}
      >
        <Carat
          stroke={isEnd ? color.stroke.neutral.border.decoration.on.strong : 'currentColor'}
          style={{ minWidth: '9px', minHeight: '9px' }}
        />
      </StyledIconButton>
    </CarouselControlsButtonWrapper>
  )
}
